import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { useLanguage } from "../i18n/LanguageContext";
import { CONTACT_EMAIL } from "../i18n/translations";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PayPalPackages = () => {
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/paypal/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ enabled: false, packages: [] }));
  }, []);

  if (!config) return null;

  const findPackage = (id) => config.packages.find((p) => p.id === id);

  const handleApprove = async (data) => {
    try {
      const res = await axios.post(`${API}/paypal/orders/${data.orderID}/capture`);
      if (res.data.status === "COMPLETED") {
        toast.success(t.packages.paySuccessTitle, { description: t.packages.paySuccessBody });
      } else {
        toast.error(t.packages.payErrorTitle, { description: t.packages.payErrorBody });
      }
    } catch {
      toast.error(t.packages.payErrorTitle, { description: t.packages.payErrorBody });
    }
  };

  return (
    <section className="py-24 border-b border-line-subtle" id="packages" data-testid="packages-section">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal className="max-w-2xl mb-16">
          <span className="text-xs font-mono uppercase tracking-widest text-gold">
            {t.packages.eyebrow}
          </span>
          <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight text-white mt-2">
            {t.packages.title}
          </h2>
          <p className="text-sm text-bone-soft mt-4">{t.packages.sub}</p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6">
          {t.packages.items.map((item, i) => {
            const pkg = findPackage(item.id);
            const payable = config.enabled && pkg;
            return (
              <Reveal key={item.id} delay={i * 120}>
                <div
                  className="bg-coal-card p-8 rounded-lg border border-line hover:border-gold/50 transition-colors h-full flex flex-col"
                  data-testid={`package-card-${item.id}`}
                >
                  <h3 className="font-display text-3xl uppercase text-white leading-tight">
                    {item.name}
                  </h3>
                  <div className="text-xs font-mono uppercase tracking-widest text-gold mt-2 mb-6">
                    {item.tagline}
                  </div>

                  <ul className="space-y-3 mb-8">
                    {item.features.map((f) => (
                      <li key={f} className="flex gap-3 text-sm text-bone-soft leading-relaxed">
                        <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto">
                    <div className="border-t border-line-subtle pt-6 mb-6">
                      <div className="font-display text-4xl text-gold">€ {item.price},-</div>
                      <div className="text-[11px] font-mono uppercase tracking-widest text-bone-mute mt-1">
                        {t.packages.durationNote}
                      </div>
                    </div>

                    <div data-testid={`paypal-slot-${item.id}`}>
                      {payable ? (
                        <PayPalScriptProvider
                          options={{ clientId: config.clientId, currency: config.currency, intent: "capture" }}
                        >
                          <PayPalButtons
                            style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                            forceReRender={[item.id, pkg.price]}
                            createOrder={async () => {
                              const res = await axios.post(`${API}/paypal/orders`, { package_id: item.id });
                              return res.data.id;
                            }}
                            onApprove={handleApprove}
                            onError={() =>
                              toast.error(t.packages.payErrorTitle, { description: t.packages.payErrorBody })
                            }
                          />
                        </PayPalScriptProvider>
                      ) : (
                        <>
                          <p className="text-xs text-bone-mute mb-4">{t.packages.comingSoon}</p>
                          <a
                            href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Pakket ${item.name}`)}`}
                            className="inline-flex justify-center items-center w-full px-5 py-3 text-xs font-semibold tracking-wider uppercase border border-gold/50 text-gold rounded hover:bg-gold hover:text-black transition-colors"
                            data-testid={`package-mailto-${item.id}`}
                          >
                            {t.packages.mailCta}
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PayPalPackages;
